<template>
  <div class="debt-installment-filter">
    <b-row class="debt-installment-filter__row mb-5">
      <!-- From Date -->
      <b-col 
        md="2" 
        class="debt-installment-filter__col"
      >
        <b-input-group>
          <date-picker
            placeholder="Từ"
            class="form-control form-control-sm debt-installment-filter__date-picker"
            :config="datePickerConfig"
            v-model="localFilters.fromDate"
            @input="onFilterChange"
            :aria-label="'Chọn ngày bắt đầu'"
          />
        </b-input-group>
      </b-col>

      <!-- To Date -->
      <b-col 
        md="2" 
        class="debt-installment-filter__col"
      >
        <b-input-group>
          <date-picker
            placeholder="Đến"
            class="form-control form-control-sm debt-installment-filter__date-picker"
            :config="datePickerConfig"
            v-model="localFilters.toDate"
            @input="onFilterChange"
            :aria-label="'Chọn ngày kết thúc'"
          />
        </b-input-group>
      </b-col>

      <!-- Installment Service Autocomplete -->
      <b-col 
        md="3" 
        class="debt-installment-filter__col"
      >
        <Autosuggest
          :model="localFilters.searchInstallment"
          :suggestions="filteredInstallments"
          placeholder="dịch vụ trả góp"
          :limit="200"
          @select="onInstallmentSelected"
          @change="onInstallmentInputChange"
          suggestionName="suggestionName"
          class="debt-installment-filter__autosuggest"
          :aria-label="'Tìm kiếm dịch vụ trả góp'"
        />
      </b-col>

      <!-- Filter Button -->
      <b-col 
        md="1" 
        class="debt-installment-filter__col"
      >
        <b-button
          class="debt-installment-filter__button"
          variant="primary"
          size="sm"
          @click="onApplyFilters"
          :disabled="isLoading"
          :aria-label="'Áp dụng bộ lọc'"
        >
          <b-spinner small v-if="isLoading"></b-spinner>
          <span v-else>Lọc</span>
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';

export default {
  name: 'DebtInstallmentFilter',
  components: {
    Autosuggest,
  },
  props: {
    /**
     * Current filter values
     */
    value: {
      type: Object,
      default: () => ({
        fromDate: null,
        toDate: null,
        searchInstallment: '',
        installmentId: null,
      }),
    },
    /**
     * Available installments for autocomplete
     */
    installments: {
      type: Array,
      default: () => [],
      validator: (installments) => Array.isArray(installments),
    },
    /**
     * Loading state for filter button
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localFilters: { ...this.value },
      filteredInstallments: [],
      datePickerConfig: {
        format: 'DD/MM/YYYY',
        useCurrent: false,
        showClear: true,
        showClose: true,
        minDate: false,
        maxDate: false,
        locale: 'vi',
        sideBySide: true,
      },
    };
  },
  watch: {
    /**
     * Watch for external value changes
     */
    value: {
      handler(newValue) {
        this.localFilters = { ...newValue };
      },
      deep: true,
    },
    /**
     * Watch for installments changes to update filtered options
     */
    installments: {
      handler(newInstallments) {
        this.filteredInstallments = [...newInstallments];
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Handle filter value changes
     */
    onFilterChange() {
      this.$emit('input', { ...this.localFilters });
    },

    /**
     * Handle installment selection from autocomplete
     * @param {Object} option - Selected installment option
     */
    onInstallmentSelected(option) {
      this.localFilters.searchInstallment = option.item.name;
      this.localFilters.installmentId = option.item.id;
      this.onFilterChange();
    },

    /**
     * Handle installment input text change
     * @param {string} text - Input text for installment search
     */
    onInstallmentInputChange(text) {
      this.localFilters.searchInstallment = text;
      this.localFilters.installmentId = null;

      // Filter installments based on input text
      this.filteredInstallments = this.installments
        .filter((item) => {
          return item.name.toLowerCase().indexOf(text.toLowerCase()) > -1;
        })
        .slice(0, 200);

      this.onFilterChange();
    },

    /**
     * Handle apply filters button click
     */
    onApplyFilters() {
      this.$emit('apply-filters', { ...this.localFilters });
    },

    /**
     * Handle Enter key press for filter application
     * @param {KeyboardEvent} event - Keyboard event
     */
    onKeyPress(event) {
      if (event.key === 'Enter') {
        this.onApplyFilters();
      }
    },
  },
  mounted() {
    // Add global Enter key listener
    window.addEventListener('keyup', this.onKeyPress);
  },
  beforeDestroy() {
    // Remove global Enter key listener
    window.removeEventListener('keyup', this.onKeyPress);
  },
};
</script>

<style lang="scss" scoped>
.debt-installment-filter {
  &__row {
    align-items: end;
  }

  &__col {
    display: flex;
    flex-direction: column;
  }

  &__date-picker {
    width: 100%;
  }

  &__autosuggest {
    width: 100%;
  }

  &__button {
    font-weight: 600;
    width: 70px;
    white-space: nowrap;
    
    &:disabled {
      cursor: not-allowed;
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .debt-installment-filter {
    &__row {
      margin-bottom: 1rem !important;
    }

    &__col {
      margin-bottom: 0.5rem;
    }

    &__button {
      width: 100%;
    }
  }
}
</style>
